import { BookOpen, Clock, PenTool, Star, User } from 'react-feather';

/*
 * The top of the home page answers one question: what do I do right now? If
 * there is already an entry today it shows how it is going and offers to carry
 * on; otherwise it offers to start. The three doors below it are the ways of
 * starting that used to be buried (the prompt generator owned two thirds of the
 * page, and writing with a mentor was reachable only from inside an entry).
 */

export const TodayCard = (props: {
  /** Text of today's entry, or null when nothing has been written today. */
  excerpt: string | null;
  wordCount: number;
  isLoadingEntries: boolean;
  openingEntry: boolean;
  onContinue: () => void;
}) => {
  const { excerpt, wordCount, isLoadingEntries, openingEntry, onContinue } = props;
  const hasWrittenToday = excerpt !== null;

  return (
    <div className="tw-rounded-3xl tw-bg-gradient-to-br tw-from-primary-50 tw-to-secondary-50 tw-p-5 lg:tw-p-8 tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-items-center lg:tw-gap-8">
      <div className="tw-flex tw-flex-col tw-gap-3 tw-min-w-0 tw-flex-1">
        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-bold tw-tracking-wide tw-text-primary-600">
          <Clock className="tw-w-4 tw-h-4 tw-shrink-0" />
          {hasWrittenToday ? `TODAY · ${wordCount} ${wordCount === 1 ? 'WORD' : 'WORDS'} SO FAR` : 'TODAY'}
        </div>
        {isLoadingEntries ? (
          <div className="tw-h-7 tw-w-2/3 tw-rounded tw-bg-white/60 tw-animate-pulse"></div>
        ) : (
          <div className="tw-text-lg lg:tw-text-xl tw-text-neutral-700 tw-max-w-prose">
            {hasWrittenToday ? `“${excerpt}”` : 'Nothing written yet today. A few lines is enough.'}
          </div>
        )}
      </div>
      <button
        className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-min-h-control tw-rounded-xl tw-bg-primary-500 tw-text-white tw-border-none tw-text-base tw-font-semibold tw-cursor-pointer disabled:tw-cursor-default disabled:tw-bg-neutral-200 disabled:tw-text-neutral-600"
        disabled={isLoadingEntries || openingEntry}
        onClick={onContinue}
      >
        {openingEntry ? (
          <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
        ) : (
          <PenTool className="tw-w-4 tw-h-4 tw-shrink-0" />
        )}
        {hasWrittenToday ? 'Continue writing' : "Start today's entry"}
      </button>
    </div>
  );
};

const Door = (props: {
  icon: JSX.Element;
  title: string;
  subtitle: string;
  premium?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const { icon, title, subtitle, premium, disabled, onClick } = props;
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="tw-flex tw-flex-1 tw-items-center tw-gap-3 tw-rounded-2xl tw-border-2 tw-border-neutral-100 tw-bg-white tw-p-4 tw-min-w-0 tw-text-left tw-cursor-pointer hover:tw-border-primary-200 disabled:tw-cursor-default disabled:tw-opacity-60"
    >
      <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary-50 tw-text-primary-600">
        {icon}
      </div>
      <div className="tw-min-w-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-base tw-font-semibold tw-text-neutral-800">{title}</span>
          {premium && (
            <span className="tw-rounded tw-bg-secondary-50 tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-secondary-600">
              Premium
            </span>
          )}
        </div>
        <div className="tw-text-sm tw-text-neutral-500">{subtitle}</div>
      </div>
    </button>
  );
};

export const StartSomethingNew = (props: {
  mentorName: string;
  showPremiumBadge: boolean;
  disabled: boolean;
  onBlankPage: () => void;
  onPrompt: () => void;
  onMentor: () => void;
}) => {
  const { mentorName, showPremiumBadge, disabled, onBlankPage, onPrompt, onMentor } = props;
  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-text-sm tw-text-neutral-500">Or start something new</div>
      <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row">
        <Door
          icon={<BookOpen className="tw-w-5 tw-h-5" />}
          title="Blank page"
          subtitle="Just start writing"
          disabled={disabled}
          onClick={onBlankPage}
        />
        <Door
          icon={<Star className="tw-w-5 tw-h-5" />}
          title="From a prompt"
          subtitle="Jumble suggests a question"
          premium={showPremiumBadge}
          onClick={onPrompt}
        />
        <Door
          icon={<User className="tw-w-5 tw-h-5" />}
          title="With a mentor"
          subtitle={`Write alongside ${mentorName}`}
          premium={showPremiumBadge}
          onClick={onMentor}
        />
      </div>
    </div>
  );
};
